import React, { useState } from 'react'
import styles from './EmailDeliveryResults.module.scss'
import { notification, Spin } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  MailOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useGetDeliveryEmailLogsQuery } from '../../api/invoice-delivery-api-client'
import {
  EmailRecipientResultDto,
  InvoiceDeliveryStatus,
} from '../../api/dto/invoice-delivery.dto'
import { getDeliveryEmailLogPreviewBlobUrl } from '../../api/invoice-delivery.api'

interface EmailDeliveryResultsProps {
  deliveryId: string
  status: InvoiceDeliveryStatus
  /**
   * When true (default) the events are wrapped in an "Email Results ▼"
   * toggle. Useful inside compact rows like the version-history panel.
   * Set to false in surfaces where the section already has its own
   * "Email Delivery" header — wrapping the content in a second collapse
   * just makes it look empty until the user clicks through.
   */
  collapsible?: boolean
}

/*
 * `Sending` is included because a targeted resend moves the row back to
 * sending while the retry is in flight. Hiding results then would drop the
 * operator's view of who already received the invoice at exactly the moment
 * they are checking on the retry.
 */
export const SHOWABLE_STATUSES = new Set<InvoiceDeliveryStatus>([
  InvoiceDeliveryStatus.Delivered,
  InvoiceDeliveryStatus.Failed,
  InvoiceDeliveryStatus.Sending,
])

const DELIVERED_GREEN = '#16a34a'
// Bounced, dropped and blocked are all "the recipient did not get it" — same
// red so the operator reads them as one category, with the label carrying the
// distinction. Opt-outs stay amber: they are a recipient choice, not a failure.
const FAILURE_RED = '#dc2626'
const OPT_OUT_AMBER = '#d97706'
const PENDING_GREY = '#6b7280'

/*
 * Keyed by the per-address outcome the backend returns. It collapses each
 * address's events into a single outcome before responding, so raw SendGrid
 * event names (`bounce`) no longer appear here — the past-tense outcome names
 * (`bounced`) do.
 */
const EVENT_CONFIG: Record<
  string,
  { icon: React.ReactNode; color: string; label: string }
> = {
  delivered: {
    icon: <CheckCircleOutlined />,
    color: DELIVERED_GREEN,
    label: 'Delivered',
  },
  bounced: {
    icon: <CloseCircleOutlined />,
    color: FAILURE_RED,
    label: 'Bounced',
  },
  dropped: {
    icon: <CloseCircleOutlined />,
    color: FAILURE_RED,
    label: 'Dropped',
  },
  blocked: {
    icon: <CloseCircleOutlined />,
    color: FAILURE_RED,
    label: 'Blocked',
  },
  unsubscribed: {
    icon: <WarningOutlined />,
    color: OPT_OUT_AMBER,
    label: 'Unsubscribed',
  },
  spam_reported: {
    icon: <WarningOutlined />,
    color: OPT_OUT_AMBER,
    label: 'Marked as spam',
  },
  no_response: {
    icon: <WarningOutlined />,
    color: PENDING_GREY,
    label: 'Awaiting confirmation',
  },
}

function formatAttemptLabel(index: number, total: number): string {
  if (total === 1) return 'Send'
  return index === 0 ? 'Initial Send' : `Resend #${index}`
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/*
 * Lifetime of a preview Blob URL after we open it in a new tab. The tab
 * has already loaded the document by the time the timeout fires, so revoking
 * the object URL here just frees memory — the tab keeps rendering its
 * already-fetched bytes. Generous enough to absorb any pop-up blocker
 * delays without keeping the URL alive forever.
 */
const PREVIEW_BLOB_LIFETIME_MS = 60_000

interface ViewSentEmailButtonProps {
  deliveryId: string
  logId: string
}

function ViewSentEmailButton({ deliveryId, logId }: ViewSentEmailButtonProps) {
  const [isLoading, setIsLoading] = useState(false)

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isLoading) return
    setIsLoading(true)
    try {
      const blobUrl = await getDeliveryEmailLogPreviewBlobUrl(deliveryId, logId)
      window.open(blobUrl, '_blank', 'noopener,noreferrer')
      setTimeout(() => URL.revokeObjectURL(blobUrl), PREVIEW_BLOB_LIFETIME_MS)
    } catch (error) {
      notification.error({
        message: 'Could not load sent email preview',
        description: (error as Error)?.message,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      className={styles.previewLink}
    >
      <EyeOutlined /> {isLoading ? 'Loading…' : 'View sent email'}
    </button>
  )
}

function RecipientRow({ recipient }: { recipient: EmailRecipientResultDto }) {
  const config = EVENT_CONFIG[recipient.event]
  return (
    <div className={styles.recipientRow} title={recipient.reason}>
      <span
        className={
          recipient.role === 'to' ? styles.roleBadgeTo : styles.roleBadgeCc
        }
      >
        {recipient.role.toUpperCase()}
      </span>
      <span className={styles.emailText}>{recipient.email}</span>
      <span
        className={styles.eventBadge}
        style={{ color: config?.color ?? '#6b7280' }}
      >
        {config?.icon}
        {config?.label ?? recipient.event}
      </span>
    </div>
  )
}

const EmailDeliveryResults: React.FC<EmailDeliveryResultsProps> = ({
  deliveryId,
  status,
  collapsible = true,
}) => {
  const [open, setOpen] = useState(false)

  const isShowable = SHOWABLE_STATUSES.has(status)
  /*
   * Skip the disclosure toggle when the parent already provides framing
   * (e.g. the drawer's "Email Delivery" section title). In that mode the
   * results are always visible, so the query fires immediately.
   */
  const isExpanded = collapsible ? open : true

  const { data, isLoading, isError } = useGetDeliveryEmailLogsQuery(
    deliveryId,
    {
      enabled: isShowable && isExpanded,
    }
  )

  if (!isShowable) return null

  const renderBody = () => (
    <div
      className={collapsible ? styles.resultBodyCollapsible : styles.resultBody}
    >
      {isLoading && (
        <div className={styles.emptyState}>
          <Spin size="small" /> Loading…
        </div>
      )}

      {isError && (
        <div className={styles.errorText}>Failed to load email results</div>
      )}

      {data && data.attempts.length === 0 && (
        <div className={styles.mutedText}>No email delivery records yet</div>
      )}

      {data &&
        data.attempts.length > 0 &&
        [...data.attempts].reverse().map((attempt, reversedIndex) => {
          const originalIndex = data.attempts.length - 1 - reversedIndex
          return (
            <div key={attempt.id} className={styles.attemptRow}>
              <div className={styles.attemptLabel}>
                {formatAttemptLabel(originalIndex, data.attempts.length)}
                <span className={styles.attemptLabelSuffix}>
                  · {formatTimestamp(attempt.createdAt)}
                </span>
                {attempt.hasPreview === true && (
                  <ViewSentEmailButton
                    deliveryId={deliveryId}
                    logId={attempt.id}
                  />
                )}
              </div>

              {attempt.recipients.map((r) => (
                <RecipientRow
                  key={`${attempt.id}-${r.email}-${r.role}`}
                  recipient={r}
                />
              ))}
            </div>
          )
        })}
    </div>
  )

  if (!collapsible) {
    return <div className={styles.resultBody}>{renderBody()}</div>
  }

  return (
    <div className={styles.collapsibleSection}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={styles.toggleButton}
      >
        <MailOutlined />
        Email Results
        <span className={styles.toggleIcon}>{open ? '▲' : '▼'}</span>
      </button>

      {open && renderBody()}
    </div>
  )
}

export { EmailDeliveryResults }
